import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ArrowDownTrayIcon, 
  ArrowUpTrayIcon, 
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  CalendarDaysIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CogIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const DataBackupPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('backup');
  const [loading, setLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(new Date().toISOString());
  const [backupHistory, setBackupHistory] = useState([]);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(true);
  const [backupFrequency, setBackupFrequency] = useState('daily');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const fileInputRef = useRef(null);

  const tabs = [
    {
      id: 'backup',
      name: 'Sauvegarde',
      icon: CloudArrowUpIcon,
      description: 'Exporter et sauvegarder les données'
    },
    {
      id: 'restore',
      name: 'Restauration',
      icon: CloudArrowDownIcon,
      description: 'Importer et restaurer les données'
    },
    {
      id: 'schedule',
      name: 'Planification',
      icon: CalendarDaysIcon,
      description: 'Configurer les sauvegardes automatiques'
    },
    {
      id: 'history',
      name: 'Historique',
      icon: ClockIcon,
      description: 'Consulter l\'historique des sauvegardes'
    }
  ];

  const dataTypes = [
    {
      id: 'members',
      name: 'Membres',
      description: 'Informations des membres, abonnements',
      icon: '👥',
      size: '2.3 MB',
      count: '156 membres'
    },
    {
      id: 'payments',
      name: 'Paiements',
      description: 'Transactions, factures, historique',
      icon: '💰',
      size: '4.7 MB',
      count: '1,234 transactions'
    },
    {
      id: 'sessions',
      name: 'Séances',
      description: 'Check-ins, séances d\'entraînement',
      icon: '🏃',
      size: '8.1 MB',
      count: '3,456 séances'
    },
    {
      id: 'equipment',
      name: 'Équipements',
      description: 'Matériel, maintenance, rapports',
      icon: '🏋️',
      size: '1.2 MB',
      count: '67 équipements'
    },
    {
      id: 'workouts',
      name: 'Entraînements',
      description: 'Plans, exercices, progression',
      icon: '💪',
      size: '3.4 MB',
      count: '24 plans'
    },
    {
      id: 'settings',
      name: 'Configurations',
      description: 'Paramètres système, préférences',
      icon: '⚙️',
      size: '0.8 MB',
      count: 'Toutes les configs'
    }
  ];

  const mockBackupHistory = [
    {
      id: 1,
      date: '2024-01-30T10:00:00Z',
      type: 'auto',
      status: 'success',
      size: '20.5 MB',
      duration: '2m 15s',
      tables: ['members', 'payments', 'sessions', 'equipment', 'workouts', 'settings']
    },
    {
      id: 2,
      date: '2024-01-29T10:00:00Z',
      type: 'auto',
      status: 'success',
      size: '20.1 MB',
      duration: '2m 08s',
      tables: ['members', 'payments', 'sessions', 'equipment', 'workouts', 'settings']
    },
    {
      id: 3,
      date: '2024-01-28T15:30:00Z',
      type: 'manual',
      status: 'success',
      size: '19.8 MB',
      duration: '1m 45s',
      tables: ['members', 'payments', 'sessions']
    },
    {
      id: 4,
      date: '2024-01-27T10:00:00Z',
      type: 'auto',
      status: 'failed',
      size: '0 MB',
      duration: '0s',
      error: 'Erreur d\'accès au fichier'
    }
  ];

  const [selectedDataTypes, setSelectedDataTypes] = useState(dataTypes.map(d => d.id));

  const handleDataTypeToggle = (typeId) => {
    setSelectedDataTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      // Simuler l'exportation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const exportData = {
        timestamp: new Date().toISOString(),
        version: '1.0',
        tables: selectedDataTypes,
        data: {
          members: selectedDataTypes.includes('members') ? [] : undefined,
          payments: selectedDataTypes.includes('payments') ? [] : undefined,
          sessions: selectedDataTypes.includes('sessions') ? [] : undefined,
          equipment: selectedDataTypes.includes('equipment') ? [] : undefined,
          workouts: selectedDataTypes.includes('workouts') ? [] : undefined,
          settings: selectedDataTypes.includes('settings') ? {} : undefined
        }
      };

      // Créer et télécharger le fichier
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gym-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setLastBackup(new Date().toISOString());
      setBackupHistory(prev => [
        {
          id: Date.now(),
          date: new Date().toISOString(),
          type: 'manual',
          status: 'success',
          size: '20.5 MB',
          duration: '2m 15s',
          tables: selectedDataTypes
        },
        ...prev
      ]);
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportData = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      // Validation basique
      if (!importData.timestamp || !importData.data) {
        throw new Error('Format de fichier invalide');
      }

      // Simuler l'importation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      alert('Données importées avec succès!');
    } catch (error) {
      alert('Erreur lors de l\'importation: ' + error.message);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleRestoreBackup = async (backup) => {
    setLoading(true);
    try {
      // Simuler la restauration
      await new Promise(resolve => setTimeout(resolve, 4000));
      alert('Sauvegarde restaurée avec succès!');
    } catch (error) {
      alert('Erreur lors de la restauration: ' + error.message);
    } finally {
      setLoading(false);
      setShowRestoreConfirm(false);
      setSelectedBackup(null);
    }
  };

  const calculateTotalSize = () => {
    return selectedDataTypes.reduce((total, typeId) => {
      const dataType = dataTypes.find(d => d.id === typeId);
      return total + parseFloat(dataType?.size || '0');
    }, 0).toFixed(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-400 bg-green-500/10';
      case 'failed': return 'text-red-400 bg-red-500/10';
      case 'pending': return 'text-yellow-400 bg-yellow-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return CheckCircleIcon;
      case 'failed': return ExclamationTriangleIcon;
      default: return InformationCircleIcon;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            <ShieldCheckIcon className="h-8 w-8 inline mr-3 text-green-400" />
            Sauvegarde & Restauration
          </h1>
          <p className="text-white/60 mt-2">
            Gestion des sauvegardes et restauration des données du système
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => handleExportData()}
            disabled={loading}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white px-4 py-2 rounded-xl transition-all duration-300 disabled:opacity-50"
          >
            {loading && activeTab === 'backup' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"></div>
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            Exporter maintenant
          </button>
        </div>
      </div>

      {/* Dernière sauvegarde */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Dernière sauvegarde</h3>
              <p className="text-white/60">
                {new Date(lastBackup).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs ${autoBackupEnabled ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
            {autoBackupEnabled ? 'Sauvegardes automatiques activées' : 'Sauvegardes automatiques désactivées'}
          </div>
        </div>
      </motion.div>

      {/* Onglets */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                <div className="text-left">
                  <div className="text-sm font-medium">{tab.name}</div>
                  <div className="text-xs opacity-80">{tab.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu de l'onglet actif */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        {/* Onglet Sauvegarde */}
        {activeTab === 'backup' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Exportation des données</h2>
            <p className="text-white/70">
              Sélectionnez les données que vous souhaitez exporter. Le fichier généré pourra être utilisé ultérieurement pour restaurer le système.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {dataTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => handleDataTypeToggle(type.id)}
                  className={`p-4 rounded-xl cursor-pointer border transition-all ${
                    selectedDataTypes.includes(type.id) 
                      ? 'bg-green-500/20 border-green-500/40'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">{type.icon}</div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{type.name}</h4>
                      <p className="text-white/60 text-sm">{type.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${
                      selectedDataTypes.includes(type.id) 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-white/30'
                    }`}>
                      {selectedDataTypes.includes(type.id) && (
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between items-center text-xs text-white/50">
                    <span>{type.size}</span>
                    <span>{type.count}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => handleExportData()}
                disabled={loading || selectedDataTypes.length === 0}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white"></div>
                ) : (
                  <ArrowDownTrayIcon className="h-5 w-5" />
                )}
                {loading ? 'Exportation en cours...' : 'Exporter les données sélectionnées'}
              </button>
            </div>
          </div>
        )}

        {/* Onglet Restauration */}
        {activeTab === 'restore' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Restauration des données</h2>
            <p className="text-white/70 mb-6">
              Importez un fichier de sauvegarde pour restaurer les données du système. Cette action est irréversible.
            </p>

            <div className="border-2 border-dashed border-white/20 rounded-xl p-8 flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
                <DocumentArrowUpIcon className="h-8 w-8 text-white/60" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">
                Déposez le fichier ici
              </h3>
              <p className="text-white/60 text-sm mb-4 text-center max-w-md">
                Format accepté: .json (fichier de sauvegarde gym-backup-YYYY-MM-DD.json)
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportData}
                accept=".json"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white"></div>
                ) : (
                  <ArrowUpTrayIcon className="h-5 w-5" />
                )}
                {loading ? 'Importation en cours...' : 'Sélectionner un fichier'}
              </button>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">
                Points de restauration récents
              </h3>
              {mockBackupHistory.length > 0 ? (
                <div className="space-y-3">
                  {mockBackupHistory.filter(b => b.status === 'success').slice(0, 3).map((backup) => (
                    <div 
                      key={backup.id} 
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all"
                      onClick={() => {
                        setSelectedBackup(backup);
                        setShowRestoreConfirm(true);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                          <CloudArrowDownIcon className="h-5 w-5 text-green-400" />
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            Sauvegarde du {new Date(backup.date).toLocaleDateString('fr-FR')}
                          </div>
                          <div className="text-white/60 text-sm">
                            {backup.type === 'auto' ? 'Automatique' : 'Manuelle'} • {backup.size} • {backup.tables.length} tables
                          </div>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-all">
                        Restaurer
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-white/60">Aucun point de restauration disponible</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Planification */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Planification des sauvegardes</h2>
            <p className="text-white/70 mb-6">
              Configurez la fréquence et les paramètres des sauvegardes automatiques.
            </p>

            <div className="space-y-6">
              {/* Activer/désactiver les sauvegardes automatiques */}
              <div className="flex justify-between items-center p-4 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <h3 className="text-white font-medium">Sauvegardes automatiques</h3>
                  <p className="text-white/60 text-sm">
                    Planifiez des sauvegardes régulières de vos données
                  </p>
                </div>
                <div 
                  className={`relative w-12 h-6 transition-colors duration-300 ease-in-out rounded-full cursor-pointer ${
                    autoBackupEnabled ? 'bg-green-500' : 'bg-white/20'
                  }`}
                  onClick={() => setAutoBackupEnabled(!autoBackupEnabled)}
                >
                  <div 
                    className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform duration-300 ease-in-out ${
                      autoBackupEnabled ? 'transform translate-x-6' : 'translate-x-0.5'
                    }`}
                  ></div>
                </div>
              </div>

              {/* Fréquence des sauvegardes */}
              {autoBackupEnabled && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-white font-medium mb-4">Fréquence des sauvegardes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {['daily', 'weekly', 'monthly'].map((freq) => (
                      <div 
                        key={freq}
                        className={`p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                          backupFrequency === freq 
                            ? 'bg-green-500/20 border border-green-500/40' 
                            : 'bg-white/10 border border-transparent hover:bg-white/20'
                        }`}
                        onClick={() => setBackupFrequency(freq)}
                      >
                        <span className="text-white">
                          {freq === 'daily' ? 'Quotidienne' : 
                           freq === 'weekly' ? 'Hebdomadaire' : 'Mensuelle'}
                        </span>
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          backupFrequency === freq 
                            ? 'border-green-500 bg-green-500' 
                            : 'border-white/30'
                        }`}>
                          {backupFrequency === freq && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {backupFrequency === 'weekly' && (
                    <div className="mt-4">
                      <h4 className="text-white/80 text-sm mb-2">Jour de la semaine</h4>
                      <div className="flex flex-wrap gap-2">
                        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => (
                          <div
                            key={day}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer ${
                              index === 1 ? 'bg-green-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                            }`}
                          >
                            {day}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {backupFrequency === 'monthly' && (
                    <div className="mt-4">
                      <h4 className="text-white/80 text-sm mb-2">Jour du mois</h4>
                      <select className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white w-full">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                          <option key={day} value={day} selected={day === 1}>
                            {day}
                          </option>
                        ))}
                        <option value="last">Dernier jour du mois</option>
                      </select>
                    </div>
                  )}

                  <div className="mt-4">
                    <h4 className="text-white/80 text-sm mb-2">Heure de la sauvegarde</h4>
                    <select className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white w-full">
                      {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                        <option key={hour} value={hour} selected={hour === 2}>
                          {hour.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                    <p className="text-white/50 text-xs mt-2">
                      Les sauvegardes automatiques s'exécuteront lorsque le système sera en ligne.
                    </p>
                  </div>
                </div>
              )}

              {/* Conservation des sauvegardes */}
              {autoBackupEnabled && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-white font-medium mb-3">Conservation des sauvegardes</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Définissez combien de temps les sauvegardes automatiques seront conservées
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">
                        Durée de conservation
                      </label>
                      <div className="flex items-center">
                        <input 
                          type="number" 
                          min="1"
                          max="365"
                          defaultValue="30"
                          className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                        />
                        <select className="ml-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white">
                          <option value="days">Jours</option>
                          <option value="weeks">Semaines</option>
                          <option value="months" selected>Mois</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm mb-2">
                        Nombre maximum de sauvegardes
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        defaultValue="10"
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex justify-end mt-6">
                <button 
                  className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white px-6 py-2 rounded-xl transition-all duration-300 shadow-lg"
                >
                  <CogIcon className="h-4 w-4" />
                  Enregistrer les paramètres
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Historique */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Historique des sauvegardes</h2>
              <select className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-sm">
                <option value="all">Toutes les sauvegardes</option>
                <option value="auto">Automatiques</option>
                <option value="manual">Manuelles</option>
                <option value="success">Réussies</option>
                <option value="failed">Échouées</option>
              </select>
            </div>

            <div className="mt-4">
              {mockBackupHistory.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <table className="min-w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Taille</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Statut</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Durée</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {mockBackupHistory.map((backup) => (
                        <tr key={backup.id} className="hover:bg-white/5 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-white">
                            {new Date(backup.date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-white/80">
                            {backup.type === 'auto' ? 'Automatique' : 'Manuelle'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-white/80">
                            {backup.size}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(backup.status)}`}>
                              {backup.status === 'success' ? 'Réussie' : 'Échouée'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-white/80">
                            {backup.duration}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex justify-end space-x-2">
                              {backup.status === 'success' && (
                                <>
                                  <button 
                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                    title="Télécharger"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                  </button>
                                  <button 
                                    className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                    title="Restaurer"
                                    onClick={() => {
                                      setSelectedBackup(backup);
                                      setShowRestoreConfirm(true);
                                    }}
                                  >
                                    <CloudArrowDownIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              <button 
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                  <ClockIcon className="h-12 w-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">Aucun historique de sauvegarde disponible</p>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Modal de confirmation de restauration */}
      {showRestoreConfirm && selectedBackup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 max-w-md w-full border border-white/20">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                <ExclamationTriangleIcon className="h-10 w-10 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Confirmer la restauration</h3>
              <p className="text-white/70">
                Vous êtes sur le point de restaurer les données à partir de la sauvegarde du {' '}
                {new Date(selectedBackup.date).toLocaleDateString('fr-FR')}. 
                Cette action remplacera toutes les données actuelles.
              </p>
            </div>
            
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-6">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <p className="text-yellow-300 text-sm">
                  Il est recommandé d'effectuer une sauvegarde avant de procéder à la restauration.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRestoreConfirm(false);
                  setSelectedBackup(null);
                }}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleRestoreBackup(selectedBackup)}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"></div>
                ) : (
                  <CloudArrowDownIcon className="h-4 w-4" />
                )}
                {loading ? 'Restauration...' : 'Confirmer la restauration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataBackupPage; 